// @flow

import React, { useEffect } from 'react';
import classNames from 'classnames';
import _ from 'lodash';
import { fileLink } from 'common/utils/file-link';
import { analytics } from 'common/utils/ga';
import { ROOT_ROUTE } from 'client/constants';
import Socials from 'client/components/common/Socialls';
import * as css from './Header.scss';

const views = {
	intro: 'intro',
	mainContent: 'mainContent',
	final: 'final',
};

type Props = {
	className?: string,
	data?: {
		logo1: FileURL,
		logo1Link: string,
		logo2: FileURL,
		logo2Big: FileURL,
		logo2Link: string,
		logo3: FileURL,
		logo3Link: string,
		isBigRightLogoDesk: boolean,
		isBigRightLogoMob: boolean,
		headerBackground: string,
		shareButtonIconColor: string,
		siteName: string,
		isTransparentHeader: boolean,
		isTransparentHeaderOnGallery: boolean,
		isTransparentHeaderOnThanks: boolean,
		view: string,
		isWithVideoBg: boolean,
		isHideLogoCenterForMobile: boolean,
	},
	isLogoVisible: boolean,
	isBackBtnVisible: boolean,
	isHeaderSociallsVisible: boolean,
	shareResults: boolean,
	onBackClick: Function,
	shareTitle: string,
	isOnHorizontalChecklist: string,
	isTotalyHideShareBtns: boolean,
};

const Header = ({
	data,
	className,
	isLogoVisible,
	onBackClick,
	isBackBtnVisible,
	isHeaderSociallsVisible,
	shareResults,
	shareTitle,
	isOnHorizontalChecklist,
	isTotalyHideShareBtns,
}: Props) => {
	const logo1Link = _.get(data, 'logo1Link', '');
	const isMakoLink = _.get(data, 'logo2Link', '') && _.get(data, 'logo2Link', '').includes('mako.co.il');
	const logo2Link = isMakoLink
		? `${_.get(data, 'logo2Link', '')}?partner=checklist_activity`
		: _.get(data, 'logo2Link', '');
	const logo3Link = _.get(data, 'logo3Link', '');
	const logo1 = _.get(data, 'logo1', {});
	const logo2 = _.get(data, 'logo2', {});
	const logo3 = _.get(data, 'logo3', {});
	const logo2Big = _.get(data, 'logo2Big', {});
	const isBigRightLogoDesk = _.get(data, 'isBigRightLogoDesk', false);
	const isBigRightLogoMob = _.get(data, 'isBigRightLogoMob', false);
	const headerBackground = _.get(data, 'headerBackground', '');
	const compainLogo = _.get(data, 'compainLogo', {});
	const shareButtonIconColor = _.get(data, 'shareButtonIconColor', '');
	const siteName = _.get(data, 'siteName', '');
	const siteLink = `${ROOT_ROUTE.replace(':id', siteName)}`;
	const isTransparentHeader = _.get(data, 'isTransparentHeader', false);
	const isTransparentHeaderOnGallery = _.get(data, 'isTransparentHeaderOnGallery', false);
	const isTransparentHeaderOnThanks = _.get(data, 'isTransparentHeaderOnThanks', false);
	const headerBg = data?.view === views.intro && isTransparentHeader;
	const withVideoBg = _.get(data, 'isWithVideoBg', false);
	const isHideLogoCenterForMobile = _.get(data, 'isHideLogoCenterForMobile', false);
	const isOnIntro = data?.view === views.intro;

	useEffect(() => {
		const header = document.getElementById('header');
		window.addEventListener('scroll', scroll);
		if (header) {
			if (data?.view === views.mainContent && isTransparentHeaderOnGallery) {
				header.classList.add('header-transparent');
			}
			if (data?.view === views.mainContent && !isTransparentHeaderOnGallery) {
				header.classList.remove('header-transparent');
			}

			if (data?.view === views.final && isTransparentHeaderOnThanks) {
				header.classList.add('header-transparent');
			}

			if (data?.view === views.final && !isTransparentHeaderOnThanks) {
				header.classList.remove('header-transparent');
			}
		}

		return () => {
			window.removeEventListener('scroll', scroll);
		};
	}, [data?.view]);

	const scroll = () => {
		const header = document.getElementById('header');
		if (header) {
			if (data?.view === views.mainContent && isTransparentHeaderOnGallery && window.scrollY > 1) {
				header.classList.remove('header-transparent');
			}

			if (data?.view === views.mainContent && isTransparentHeaderOnGallery && window.scrollY < 1) {
				header.classList.add('header-transparent');
			}

			if (data?.view === views.final && isTransparentHeaderOnThanks && window.scrollY > 1) {
				header.classList.remove('header-transparent');
			}

			if (data?.view === views.final && isTransparentHeaderOnThanks && window.scrollY < 1) {
				header.classList.add('header-transparent');
			}
		}
	};

	const onMakoLogoClick = () => {
		analytics.gtag.event('Click mako logo');
	};

	const onPartnerLogoClick = () => {
		analytics.gtag.event('Click customer logo');
	};

	const onProjectLogoClick = () => {
		analytics.gtag.event('Click Activity logo');
	};

	return (
		<div
			className={classNames(css.header, className, isOnIntro && withVideoBg && css.onIntroWithVideo)}
			id="header"
			style={{ backgroundColor: headerBg ? 'transparent' : headerBackground }}
		>
			<div className={classNames(css.headerWrapper, isOnHorizontalChecklist && css.onHorizontal)}>
				<a
					href={siteLink}
					className={classNames(
						css.logoCampaignCenter,
						isLogoVisible ? css.in : css.out,
						isHideLogoCenterForMobile && css.hideOnMobile
					)}
					onClick={onProjectLogoClick}
				>
					<img src={fileLink(compainLogo)} alt="" />
				</a>
				<div className={css.right}>
					<a
						href={logo2Link}
						target="_blank"
						rel="noopener noreferrer"
						className={classNames(
							css.logo2Desk,
							isBigRightLogoDesk && css.big,
							!isBigRightLogoDesk && css.small
						)}
						onClick={onMakoLogoClick}
					>
						<img
							src={isBigRightLogoDesk ? fileLink(logo2Big) : fileLink(logo2)}
							alt=""
							className={css.logoDesk}
						/>
					</a>

					<a
						href={logo2Link}
						target="_blank"
						rel="noopener noreferrer"
						className={classNames(
							css.logo2Mob,
							isBigRightLogoMob && css.big,
							!isBigRightLogoMob && css.small
						)}
						onClick={onMakoLogoClick}
					>
						<img
							src={isBigRightLogoMob ? fileLink(logo2Big) : fileLink(logo2)}
							alt=""
							className={css.logoMob}
						/>
					</a>

					<a
						href={logo1Link}
						target="_blank"
						rel="noopener noreferrer"
						className={css.logo1}
						onClick={onPartnerLogoClick}
					>
						<img src={fileLink(logo1)} alt="" />
					</a>
					<a
						href={logo3Link}
						target="_blank"
						rel="noopener noreferrer"
						className={css.logo3}
						onClick={onPartnerLogoClick}
					>
						<img src={fileLink(logo3)} alt="" />
					</a>
				</div>
				{!isTotalyHideShareBtns && (
					<Socials
						onBackClick={onBackClick}
						isBackBtnVisible={isBackBtnVisible}
						isHeaderSociallsVisible={isHeaderSociallsVisible}
						// shareBtnIconColor={shareBtnIconColor}
						shareResults={shareResults}
						headerBackground={headerBackground}
						iconsColor={shareButtonIconColor}
						inHeaderLocated
						generalShareTitle={shareTitle}
					/>
				)}
			</div>
		</div>
	);
};

Header.defaultProps = {
	className: '',
	data: {},
};

// const mapState = (state: State) => ({
// 	data: _.get(state, 'page.data', {}),
// });

export { Header };
